import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './SidebarItemMenu.css';

/**
 * SidebarItemMenu — ChatGPT-style 3-dot dropdown menu for sidebar items.
 * Shows on hover over the parent card. Provides Rename, Pin, and Delete actions.
 * Renders the dropdown in a portal so it is never clipped by ancestor overflow.
 *
 * Props:
 *   onRename   — callback; if provided, shows "Rename" option
 *   onPin      — callback(isPinned); if provided, shows "Pin" / "Unpin" option
 *   onDelete   — callback; if provided, shows "Delete" option
 *   isPinned   — boolean; controls pin/unpin label
 */
const SidebarItemMenu = ({ onRename, onPin, onDelete, isPinned = false }) => {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  // Position the portal dropdown relative to the trigger button
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    // Estimate dropdown height; flip upward if it would overflow the viewport
    const estimatedHeight = 140;
    const fitsBelow = rect.bottom + 4 + estimatedHeight <= window.innerHeight;
    const top = fitsBelow ? rect.bottom + 4 : rect.top - 4 - estimatedHeight;
    setDropdownPos({ top: Math.max(4, top), left: rect.right });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Close on scroll of any ancestor (dropdown position would be stale)
  useEffect(() => {
    if (!open) return;
    const handleScroll = () => setOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open]);

  const handleAction = useCallback((action) => (e) => {
    e.stopPropagation();
    setOpen(false);
    action();
  }, []);

  return (
    <div className="sim-container">
      <button
        ref={btnRef}
        className={`sim-trigger ${open ? 'sim-trigger--open' : ''}`}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        aria-label="More options"
        tabIndex={-1}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.2" />
          <circle cx="8" cy="8" r="1.2" />
          <circle cx="8" cy="13" r="1.2" />
        </svg>
      </button>

      {open && dropdownPos && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="sim-dropdown sim-dropdown--portal"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {onRename && (
            <button className="sim-option" onClick={handleAction(onRename)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
              <span>Rename</span>
            </button>
          )}
          {onPin && (
            <button className="sim-option" onClick={handleAction(() => onPin(!isPinned))}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 17v5" />
                <path d="M9 2h6l-1 7h4l-5 8h-2l-5-8h4Z" />
              </svg>
              <span>{isPinned ? 'Unpin' : 'Pin'}</span>
            </button>
          )}
          {onDelete && (
            <button className="sim-option sim-option--danger" onClick={handleAction(onDelete)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
              <span>Delete</span>
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SidebarItemMenu;
